import * as React from 'react';

import { enumVehicleDetails } from '@rematter/modules/trade/types/scaleTickets';
import { cn, ConsoleTouchDialog, TouchTextField } from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucideCarFront } from 'lucide-react';

import { useDialogModalStyle } from '../hooks/useDialogModalStyle';

import { TouchDialogHeader } from './TouchDialogHeader';

import { $inventoryItems } from '~/modules/trade/_state/loadForm.state';
import {
  $closeVehicleDetails,
  $vehicleDetailsState,
} from '~/modules/trade/_state/materialsInterface.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { useTheme } from '~/shared/hooks';

export type VehicleDetailProps = {
  id: string;
  itemId?: string | null;
  vin?: string;
  make?: string;
  model?: string;
  year?: string;
};

type EditVehicleDialogProps = {
  onUpdateValues: (data: VehicleDetailProps) => void;
};

//
// ⚛️ ⚛️ ⚛️ EDIT VEHICLE DIALOG ⚛️ ⚛️ ⚛️
//
export const EditVehicleDialog = ({
  onUpdateValues,
}: EditVehicleDialogProps) => {
  // Translations
  const t = useTranslations();
  const vehDetails = t.scaleTicketPro.vehicleDetails;

  // States
  const [view, setView] = React.useState(enumVehicleDetails.vin);

  // Atoms
  const [vehicleDetailsState, setVehicleDetailsState] =
    useAtom($vehicleDetailsState);
  const closeVehicleDetails = useSetAtom($closeVehicleDetails);
  const { data: inventoryItemsData } = useAtomValue($inventoryItems);

  const { isOpen, lineItemId, itemId, vin, make, model, year } =
    vehicleDetailsState;

  // Other data hooks
  const { effectiveColorScheme } = useTheme();
  const modalStyle = useDialogModalStyle();

  // Memoized values
  const inventoryItem = React.useMemo(
    () => inventoryItemsData?.allItems?.find((item) => item.id === itemId),
    [inventoryItemsData?.allItems, itemId],
  );

  // Callbacks
  const applyUpdate = React.useCallback(
    (
      overrides: Partial<{
        vin: string | null;
        make: string | null;
        model: string | null;
        year: number | null;
      }>,
    ) => {
      if (!lineItemId) return;
      const merged = {
        vin: overrides.vin !== undefined ? overrides.vin : vin,
        make: overrides.make !== undefined ? overrides.make : make,
        model: overrides.model !== undefined ? overrides.model : model,
        year: overrides.year !== undefined ? overrides.year : year,
      };
      onUpdateValues({
        id: lineItemId,
        vin: merged.vin ?? undefined,
        make: merged.make ?? undefined,
        model: merged.model ?? undefined,
        year: merged.year?.toString() ?? undefined,
      });
    },
    [lineItemId, vin, make, model, year, onUpdateValues],
  );

  return (
    <ConsoleTouchDialog
      touchModeEnabled={true}
      effectiveColorScheme={effectiveColorScheme}
      isOpen={isOpen}
      onOpenChange={closeVehicleDetails}
      className="@EditVehicleDialog"
      dialogClassName={cn('h-full w-full max-w-full')}
      classNameHeader="!pb-0"
      contentStyle={modalStyle}
      header={
        <TouchDialogHeader
          icon={<LucideCarFront size={16} />}
          iconColor="primary"
          title={inventoryItem?.name ?? ''}
          subtitle={inventoryItem?.itemGroup?.name}
          tabs={Object.values(enumVehicleDetails).map((option) => ({
            key: option,
            label: vehDetails.tabs[enumVehicleDetails[option]],
          }))}
          selectedTab={view}
          onTabChange={(key) => setView(key as enumVehicleDetails)}
        />
      }
    >
      {view === enumVehicleDetails.vin && (
        <TouchTextField
          className="h-30 text-5xl"
          value={vin ?? undefined}
          onChange={(v) => {
            setVehicleDetailsState((prev) => ({ ...prev, vin: v }));
            applyUpdate({ vin: v ?? null });
          }}
          fillLength={17}
          fillChar="0"
          aria-label={vehDetails.tabs.vin}
          touchModeEnabled
        />
      )}

      {view === enumVehicleDetails.make && (
        <TouchTextField
          className="h-30 text-5xl"
          value={make ?? undefined}
          onChange={(v) => {
            setVehicleDetailsState((prev) => ({ ...prev, make: v }));
            applyUpdate({ make: v ?? null });
          }}
          placeholder={vehDetails.tabs.make}
          aria-label={vehDetails.tabs.make}
          touchModeEnabled
        />
      )}

      {view === enumVehicleDetails.model && (
        <TouchTextField
          className="h-30 text-5xl"
          value={model ?? undefined}
          onChange={(v) => {
            setVehicleDetailsState((prev) => ({ ...prev, model: v }));
            applyUpdate({ model: v ?? null });
          }}
          placeholder={vehDetails.tabs.model}
          aria-label={vehDetails.tabs.model}
          touchModeEnabled
        />
      )}

      {view === enumVehicleDetails.year && (
        <TouchTextField
          className="h-30 text-5xl"
          value={year?.toString() ?? undefined}
          onChange={(v) => {
            const parsed = v ? parseInt(v, 10) : null;
            setVehicleDetailsState((prev) => ({ ...prev, year: parsed }));
            applyUpdate({ year: parsed });
          }}
          fillLength={4}
          fillChar="0"
          aria-label={vehDetails.tabs.year}
          touchModeEnabled
        />
      )}
    </ConsoleTouchDialog>
  );
};
